import { defineStore } from "pinia"
import type { CareerRequest } from "~/models/career"
import { type Category } from "~/models/category"
import type { Company } from "~/models/company"
import type { Payroll } from "~/models/payroll"
import type { PerformanceReview } from "~/models/performance-review"
import type { Post } from "~/models/post"
import type { Slide } from "~/models/slide"
import { type User } from "~/models/user"
import { type ApiResponse } from "~/types/api"

export const useGeneralStore = defineStore("general", () => {
	const app = useNuxtApp()
	const metaStore = useMetaStore()

	const state = {
		socialLinks: ref([
			{
				id: "email",
				title: app.$i18n.t("email"),
				value: metaStore.metas?.[META_KEYS.META_CONTACT_EMAIL] || undefined,
				icon: "mdi:email",
				link: metaStore.metas?.[META_KEYS.META_CONTACT_EMAIL]
					? "mailto:" + metaStore.metas?.[META_KEYS.META_CONTACT_EMAIL]
					: undefined
			},
			{
				id: "instagram",
				title: app.$i18n.t("instagram"),
				value: metaStore.metas?.[META_KEYS.META_SOCIAL_INSTAGRAM] || undefined,
				icon: "mdi:instagram",
				link: metaStore.metas?.[META_KEYS.META_SOCIAL_INSTAGRAM]
					? `https://instagram.com/` + (metaStore.metas?.[META_KEYS.META_SOCIAL_INSTAGRAM] as string)
					: undefined
			},
			{
				id: "whatsapp",
				title: app.$i18n.t("whatsapp"),
				value: metaStore.metas?.[META_KEYS.META_SOCIAL_WHATSAPP] || undefined,
				icon: "mdi:whatsapp",
				link: metaStore.metas?.[META_KEYS.META_SOCIAL_WHATSAPP]
					? `https://wa.me/` +
						(metaStore.metas?.[META_KEYS.META_SOCIAL_WHATSAPP] as string)
							?.replace(/\D/g, "")
							?.replace(/^0+/g, "")
					: undefined
			},
			{
				id: "address",
				title: app.$i18n.t("address"),
				value: metaStore.metas?.[META_KEYS.META_CONTACT_ADDRESS] || undefined,
				icon: "mdi:map-marker",
				link: undefined
			}
		] as const)
	}

	const bootstrap = async (formData: BootstrapRequest) => {
		const data = await $fetch<BootstrapResponse>(Bootstrap.url(), {
			...useFetchConfig(),
			method: Bootstrap.method,
			query: formData
		})
		return data.data
	}

	const homepage = async (formData?: HomepageRequest) => {
		const data = await $fetch<HomepageResponse>(Homepage.url(), {
			...useFetchConfig(),
			method: Homepage.method,
			query: formData
		})
		return data.data
	}

	const dashboard = async (formData: DashboardRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<DashboardResponse>(Dashboard.url(), {
			...useFetchConfig(),
			method: Dashboard.method,
			query: formData
		})
		if (data.data.company?.id == companyStore.companyId) {
			companyStore.company = data.data.company
		}
		return data.data
	}

	const staffDashboard = async (formData: StaffDashboardRequest) => {
		const companyStore = useCompanyStore()
		const data = await $fetch<StaffDashboardResponse>(StaffDashboard.url(), {
			...useFetchConfig(),
			method: StaffDashboard.method,
			query: formData
		})
		if (data.data.company?.id == companyStore.companyId) {
			companyStore.company = data.data.company
		}
		return data.data
	}

	return {
		...state,
		bootstrap,
		homepage,
		dashboard,
		staffDashboard
	}
})

// general :: bootstrap
export interface BootstrapRequest {
	profile?: boolean
	metas?: boolean
	blockchains?: boolean
	currencies?: boolean
	categories?: boolean
	wallets?: boolean
	company_id?: number
	hostname?: string
}
export interface BootstrapResponse extends ApiResponse {
	data: {
		profile?: User
		metas?: any
		categories?: Category[]
		company?: Company
		payroll_domain_company?: Company
		career_domain_company?: Company
	}
}
const Bootstrap = {
	method: "GET" as const,
	url: () => "bootstrap"
}

// general :: homepage
export interface HomepageRequest {}
export interface HomepageResponse extends ApiResponse {
	data: {
		slides: Slide[]
		latest_articles: Post[]
	}
}
const Homepage = {
	method: "GET" as const,
	url: () => "homepage"
}

export const useGeneralApiUrl = () => ({
	Bootstrap,
	Homepage
})

// general :: dashboard
export interface DashboardRequest {
	company_id?: number
}
export interface DashboardResponse extends ApiResponse {
	data: {
		companies: Company[]
		users: User[] | null
		payrolls: Payroll[] | null
		performance_reviews: PerformanceReview[] | null
		career_requests: CareerRequest[] | null
		company: Company | null
	}
}
const Dashboard = {
	method: "GET" as const,
	url: () => "dashboard"
}

// general :: staff dashboard
export interface StaffDashboardRequest {}
export interface StaffDashboardResponse extends ApiResponse {
	data: {
		payrolls: Payroll[]
		performance_reviews: PerformanceReview[]
		company: Company
	}
}

const StaffDashboard = {
	method: "GET" as const,
	url: () => "staff-dashboard"
}
